/**
 * Entry point, and the only place that handles "this build was never configured".
 *
 * THE HOUSE STYLES COME FIRST. `./design/styles` puts the Inter Variable font, every
 * `--lg-*` token, the base rules and the primitive classes on the page before any
 * component (or MUI's own styles) is imported, which is the cascade the Loop Design
 * System expects (design/README.md, Install, "MUI 7").
 *
 * The app is imported DYNAMICALLY so that a throw during module evaluation — which
 * is what `env.ts` does when a required VITE_ variable is missing — becomes a
 * readable screen instead of a blank page. That screen is the house StandalonePage
 * (bootFailure.tsx, "The console could not start"), itself imported dynamically in
 * the catch: React is loaded by then, since only `./bootstrap` failed.
 *
 * THE LAST RESORT is `renderConfigError`, written in plain DOM, importing nothing:
 * it runs only when the boot-failure page cannot be imported or rendered, because
 * whatever failed might be the component library itself. It is still dark and on
 * the house palette: it reads the `--lg-*` custom properties the design stylesheet
 * has already put on `:root`, and carries no colour literals of its own.
 *
 * A DEPLOY UNDER AN OPEN TAB. The first thing this file does is listen for Vite's
 * `vite:preloadError`: a tab opened before a Hosting deploy asks for lazy chunks the
 * new deploy no longer serves, and the page reloads once to get the new build
 * (lib/staleChunkReload.ts). A failure the reload does not cure is caught by the
 * error boundaries (layout/RouteErrorBoundary.tsx), which offer "Reload" by hand.
 */

import "./design/styles";

import { StrictMode } from "react";
import { createRoot } from "react-dom/client";

import { BOOT_FAILURE_TEXT } from "./bootFailureText";
import { installStaleChunkReload } from "./lib/staleChunkReload";

// Before anything is imported lazily, the bootstrap included.
installStaleChunkReload();

const mountPoint = document.getElementById("root");

if (mountPoint === null) {
    throw new Error("index.html has no #root element.");
}

// Re-bound after the null check so the non-null type survives into the closures below.
const container: HTMLElement = mountPoint;

/** The last resort: a dependency-free failure screen. It names the actual missing thing. */
function renderConfigError(message: string): void {
    container.innerHTML = "";

    const wrapper = document.createElement("div");
    wrapper.setAttribute("role", "alert");
    wrapper.style.cssText = [
        "max-width:640px",
        "margin:64px auto",
        "padding:var(--lg-space-6)",
        "border:1px solid var(--lg-line-strong)",
        "border-radius:var(--lg-radius-md)",
        "font-family:var(--lg-font-sans)",
        "line-height:1.5",
        "color:var(--lg-text)",
        "background:var(--lg-surface)"
    ].join(";");

    const heading = document.createElement("h1");
    heading.textContent = BOOT_FAILURE_TEXT.title;
    heading.style.cssText = "font-size:var(--lg-font-size-xl);font-weight:600;margin:0 0 12px";

    const intro = document.createElement("p");
    intro.textContent = BOOT_FAILURE_TEXT.intro;
    intro.style.cssText = "margin:0 0 16px;color:var(--lg-text-secondary)";

    const detail = document.createElement("pre");
    detail.textContent = message;
    detail.style.cssText = [
        "margin:0 0 16px",
        "padding:12px",
        "background:var(--lg-inset)",
        "border-radius:var(--lg-radius-sm)",
        "white-space:pre-wrap",
        "word-break:break-word",
        "font-family:var(--lg-font-mono)",
        "font-size:var(--lg-font-size-sm)"
    ].join(";");

    const help = document.createElement("p");
    help.textContent = BOOT_FAILURE_TEXT.help;
    help.style.cssText = "margin:0;font-size:var(--lg-font-size-md);color:var(--lg-text-dimmed)";

    wrapper.append(heading, intro, detail, help);
    container.append(wrapper);
}

/** The house boot-failure page, or the plain screen when that page cannot load or render. */
async function showBootFailure(message: string): Promise<void> {
    try {
        const { BootFailurePage } = await import("./bootFailure");
        await new Promise<void>((resolve, reject) => {
            const root = createRoot(container, {
                onUncaughtError: (error: unknown) => {
                    // After React has finished with the failed render: free the container, then hand over.
                    setTimeout(() => {
                        root.unmount();
                        reject(error instanceof Error ? error : new Error(String(error)));
                    }, 0);
                }
            });
            root.render(
                <StrictMode>
                    <BootFailurePage message={message} onShown={resolve} />
                </StrictMode>
            );
        });
    } catch (pageError: unknown) {
        console.error("[bootstrap] The boot-failure page could not render.", pageError);
        renderConfigError(message);
    }
}

void import("./bootstrap")
    .then(({ Bootstrap }) => {
        createRoot(container).render(
            <StrictMode>
                <Bootstrap />
            </StrictMode>
        );
    })
    .catch((error: unknown) => {
        console.error("[bootstrap] The console could not start.", error);
        return showBootFailure(error instanceof Error ? error.message : String(error));
    });
